import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Icon, IconProps } from '@brokerbay/elements-web';
import { Button as ButtonV2 } from '@bb/elements-web-rebranding';
import { IListing } from '@bb/domain/dist/types';
import errorReporter from '@app/errorReporter';
import { AddListingToTourModalContext, AuthContextType } from 'contexts';
import styled from 'styled-components';
import { getNoMoreShowings } from 'api/AppointmentConfigController';
import { Dropdown } from 'components/Dropdown';
import { Menu, MenuItem } from 'components/Menu';
import { Tooltip } from 'components/Tooltip';
import { buildCssProp, featureEnabled } from 'utils';
import { theme, Button, message, Spin } from 'components';
import {
  createContextPortal,
  ContextPortalID,
} from '../../Router/pages/TourEditPage/hooks';
import ListingSummaryProps from '../../types/listingSummaryPropsType';
import { TOOLTIP_OPTIONS } from './constants';
import { TOURS_DISABLED_SHOWING_STATUS } from '../../Router/pages/TourEditPage/constants';

type Options = {
  icon: IconProps['icon'];
  label: string;
  key: string;
  // Props type in shouldShow can be updated to include more options
  shouldShow: (props: DropdownBookingActionsType['isAutoConfirm']) => boolean;
}[];

const IconWrapper = styled.div``;

const BOOKING_DROPDOWN_ITEMS: Options = [
  {
    label: 'Book showing',
    key: 'book',
    icon: 'calendarOutlined',
    shouldShow: isAutoConfirm => !isAutoConfirm,
  },
  {
    label: 'Request showing',
    key: 'book',
    icon: 'calendarOutlined',
    shouldShow: isAutoConfirm => isAutoConfirm,
  },
];

type DropdownBookingActionsType = {
  isAutoConfirm: boolean;
  isDisabledBtns: boolean;
  listing: IListing & { id: string };
  isAgent: boolean;
  isListingAgent: boolean;
  isUnverified: boolean;
  org: AuthContextType['auth']['org'];
  handleBookModal: () => void;
  preBookingInformation: string | undefined;
};

export const DropdownBookingActions = ({
  isAutoConfirm,
  isDisabledBtns,
  listing,
  isAgent,
  isListingAgent,
  isUnverified,
  org,
  handleBookModal,
  preBookingInformation,
}: DropdownBookingActionsType) => {
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  return (
    <Tooltip title={isDisabledBtns && TOOLTIP_OPTIONS.showing}>
      <span>
        <Dropdown
          placement="bottomRight"
          trigger={['click']}
          dropdownRender={() => (
            <OptionsMenu
              options={BOOKING_DROPDOWN_ITEMS}
              isAutoConfirm={isAutoConfirm}
              listing={listing}
              isAgent={isAgent}
              isListingAgent={isListingAgent}
              isUnverified={isUnverified}
              org={org}
              handleBookModal={handleBookModal}
              preBookingInformation={preBookingInformation}
            />
          )}
          disabled={isDisabledBtns}
        >
          {featureEnabled('supraRebrandingFeature', 1) ? (
            <StyledDropdownButtonV2
              dataCy="dropdown-booking-actions"
              onClick={() => setDropdownOpen(prev => !prev)}
              disabled={isDisabledBtns}
              $disabled={isDisabledBtns}
            >
              Showing actions
              {!dropdownOpen && (
                <Icon
                  icon="arrowDownOutlined"
                  color={`${isDisabledBtns ? 'light' : 'white'}`}
                />
              )}
              {dropdownOpen && (
                <Icon
                  icon="arrowUpOutlined"
                  color={`${isDisabledBtns ? 'light' : 'white'}`}
                />
              )}
            </StyledDropdownButtonV2>
          ) : (
            <StyledDropdownButton
              type="primary"
              data-cy="dropdown-booking-actions"
              onClick={() => setDropdownOpen(prev => !prev)}
              disabled={isDisabledBtns}
              $disabled={isDisabledBtns}
            >
              Showing actions
              {!dropdownOpen && (
                <Icon
                  icon="arrowDownOutlined"
                  color={`${isDisabledBtns ? 'light' : 'white'}`}
                />
              )}
              {dropdownOpen && (
                <Icon
                  icon="arrowUpOutlined"
                  color={`${isDisabledBtns ? 'light' : 'white'}`}
                />
              )}
            </StyledDropdownButton>
          )}
        </Dropdown>
      </span>
    </Tooltip>
  );
};

const OptionsMenu: React.FC<
  { options: Options } & Omit<DropdownBookingActionsType, 'isDisabledBtns'>
> = ({
  options,
  isAutoConfirm,
  listing,
  isAgent,
  isListingAgent,
  isUnverified,
  org,
  handleBookModal,
  preBookingInformation,
}) => {
  const [noMoreShowings, setNoMoreShowings] = useState(false);
  const [addToTourButtonLoading, setAddToTourButtonLoading] = useState(true);
  const showingUnavailable = TOURS_DISABLED_SHOWING_STATUS.includes(
    listing.availability,
  );

  return (
    <Menu selectable={false}>
      {/* Book showing buttons */}
      {options.map(
        ({ icon, label, key, shouldShow }) =>
          shouldShow(isAutoConfirm) && (
            <>
              {featureEnabled('preBookingNotificationFeature') ? (
                <MenuItem
                  key={key}
                  onClick={handleBookModal}
                  icon={<Icon icon={icon} />}
                >
                  {label}
                </MenuItem>
              ) : (
                <MenuItem
                  key={key}
                  onClick={handleBookModal}
                  icon={<Icon icon={icon} />}
                  href={
                    org &&
                    org.features.includes('APPOINTMENT') &&
                    !(isAgent && !isListingAgent && isUnverified)
                      ? `#/listing/${listing.id}/appointments/book`
                      : undefined
                  }
                >
                  {label}
                </MenuItem>
              )}
            </>
          ),
      )}
      {/* Add to tour button */}
      <MenuItem
        key="tour"
        disabled={
          noMoreShowings || addToTourButtonLoading || showingUnavailable
        }
      >
        <AddToTourButtonWithContext
          listing={listing as unknown as ListingSummaryProps}
          setNoMoreShowings={setNoMoreShowings}
          setLoading={setAddToTourButtonLoading}
          noMoreShowings={noMoreShowings}
          loading={addToTourButtonLoading}
          showingUnavailable={showingUnavailable}
          preBookingInformation={preBookingInformation}
        />
      </MenuItem>
    </Menu>
  );
};

type AddToTourButtonWithContextProps = {
  listing: ListingSummaryProps;
  setNoMoreShowings: React.Dispatch<React.SetStateAction<boolean>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  noMoreShowings: boolean;
  loading: boolean;
  showingUnavailable: boolean;
  preBookingInformation: string | undefined;
};

// This is a wrapper to avoid the createContextPortal from conflicting with MenuItem
const AddToTourButtonWithContext = createContextPortal(
  ContextPortalID.ADD_LISTING_TO_TOUR_CONTEXT,
  ({
    listing: listingFromListingPage,
    setNoMoreShowings,
    setLoading,
    noMoreShowings,
    loading,
    showingUnavailable,
    preBookingInformation,
  }: AddToTourButtonWithContextProps) => {
    const {
      setModalVisible,
      setListing,
      listing: listingFromUniversalSearch,
      modalVisible,
    } = useContext(AddListingToTourModalContext);

    useEffect(
      () => () => {
        if (modalVisible) setModalVisible(false);
      },
      [],
    );
    useEffect(() => {
      (async () => {
        try {
          setLoading(true);
          setNoMoreShowings(await getNoMoreShowings(listingFromListingPage.id));
          setLoading(false);
        } catch (err) {
          message.error('Error fetching listing config');
          errorReporter.error(
            `Failed to fetch noMoreShowings when initializing addToTour button: ${err}`,
          );
        }
      })();
    }, [listingFromListingPage]);

    const addToTourTooltipText = useMemo(() => {
      if (noMoreShowings) return TOOLTIP_OPTIONS.tourNoMoreShowings;
      if (showingUnavailable) return TOOLTIP_OPTIONS.tourShowingUnavailable;
      return '';
    }, [showingUnavailable, noMoreShowings]);

    return (
      <StyledAddToTourButton
        onClick={() => {
          if (
            showingUnavailable ||
            noMoreShowings ||
            loading ||
            listingFromUniversalSearch
          )
            return;
          const enhancedListing = {
            ...listingFromListingPage,
            preBookingInformation,
          };
          setListing(enhancedListing);
          setModalVisible(true);
        }}
        data-cy="open-add-to-tour-modal-button"
      >
        <Tooltip
          title={(noMoreShowings || showingUnavailable) && addToTourTooltipText}
        >
          <StyledTooltipContainer>
            <IconWrapper>
              {!loading && <StyledIcon size={12} icon="plusOutlined" />}
              {loading && <Spin size="small" />}
            </IconWrapper>
            <div>Add to tour</div>
          </StyledTooltipContainer>
        </Tooltip>
      </StyledAddToTourButton>
    );
  },
);

const StyledDropdownButton = styled(Button)<{ $disabled: boolean }>`
  && {
    display: flex;
    align-items: center;
    line-height: 18px;
    font-size: 14px;
    text-align: center;
    border-radius: 7px;
    ${({ $disabled }) => buildCssProp('color', !$disabled && '#FFFFFF')}
  }
`;

const StyledDropdownButtonV2 = styled(ButtonV2)<{ $disabled: boolean }>`
  && {
    display: flex;
    align-items: center;
    line-height: 16px;
    font-size: 14px;
    text-align: center;
    border-radius: 7px;
    font-weight: 400;
    min-width: auto;
    ${({ $disabled }) => buildCssProp('color', !$disabled && '#FFFFFF')}
  }
`;

const hoverStyles = `
  fill: ${theme.textColorSecondary};
  color: ${theme.textColorSecondary};
`;

const StyledIcon = styled(Icon)``;

const StyledAddToTourButton = styled.div`
  && {
    color: ${theme.menuItemColor};
    display: flex;
    align-items: center;
    border-radius: ${theme.getBorderRadius('large')};
    border-radius: 10px;
    &:hover {
      background-color: ${theme.gray3};
      > *,
      .menu-item-icon-wrapper > ${StyledIcon} > svg {
        ${hoverStyles}
      }
    }
    .ant-dropdown-menu-title-content > * {
      padding: 10px 12px;
      margin: 0;
      color: ${theme.menuItemColor};
      display: flex;
      align-items: center;
      width: 100%;

      &:hover {
        ${hoverStyles}
      }
    }
    ${IconWrapper} {
      margin-right: 10px;
      &:hover ${IconWrapper} > ${StyledIcon} > svg {
        ${hoverStyles}
      }
    }
  }
`;

const StyledTooltipContainer = styled.div`
  display: flex;
`;
